import type { PhotographerQueryParams, PhotoQueryParams } from './types';

export interface AdminSearchDictionaries {
  states?: Map<string, string>;
  countries?: Map<string, string>;
  types?: Map<string, string>;
  categories?: Map<string, string>;
  themes?: Map<string, string>;
  tags?: Map<string, string>;
}

const resolveOptionalId = (
  map: Map<string, string> | undefined,
  value: string | undefined,
  kind: string,
): number | undefined => {
  if (!value) return undefined;
  if (!map) throw new Error(`Missing ${kind} dictionary`);
  const id = map.get(value);
  if (!id) throw new Error(`Unknown ${kind} "${value}"`);
  return Number(id);
};

const resolveIds = (
  map: Map<string, string> | undefined,
  values: string[] | undefined,
  kind: string,
): number[] | undefined => {
  if (!values?.length) return undefined;
  if (!map) throw new Error(`Missing ${kind} dictionary`);
  return values.map((value) => {
    const id = map.get(value);
    if (!id) throw new Error(`Unknown ${kind} "${value}"`);
    return Number(id);
  });
};

export const buildAdminPhotoSearchInput = (
  params: PhotoQueryParams,
  dictionaries: AdminSearchDictionaries,
) => {
  const { filters, pagination, sort } = params;

  return {
    stateIds: resolveIds(dictionaries.states, filters.states, 'photo state'),
    categoryId: resolveOptionalId(
      dictionaries.categories,
      filters.category,
      'photo category',
    ),
    typeId: resolveOptionalId(dictionaries.types, filters.type, 'photo type'),
    excludeTypeId: resolveOptionalId(
      dictionaries.types,
      filters.excludeType,
      'photo type',
    ),
    themeIds: resolveIds(dictionaries.themes, filters.themes, 'photo theme'),
    tagIds: resolveIds(dictionaries.tags, filters.tags, 'photo tag'),
    photographerId: filters.photographerId
      ? Number(filters.photographerId)
      : undefined,
    search: filters.search?.trim() || undefined,
    uploadedFrom: filters.uploadedFrom,
    uploadedTo: filters.uploadedTo,
    sortField: sort.field,
    sortDirection: sort.direction,
    page: pagination.page,
    pageSize: pagination.limit,
  };
};

export const buildAdminPhotographerSearchInput = (
  params: PhotographerQueryParams,
  dictionaries: AdminSearchDictionaries,
) => {
  const { filters, pagination, sort } = params;

  return {
    stateIds: resolveIds(
      dictionaries.states,
      filters.states,
      'photographer state',
    ),
    tier: filters.tier,
    countryId: resolveOptionalId(
      dictionaries.countries,
      filters.country,
      'country',
    ),
    search: filters.search?.trim() || undefined,
    includeWithoutPhotos: !filters.withPhotosOnly,
    sortField: sort.field,
    sortDirection: sort.direction,
    page: pagination.page,
    pageSize: pagination.limit,
  };
};
